import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";
import { ArrowLeft, X } from "lucide-react";
import { DashboardCard } from "@/components/ds/DashboardCard";
import { useMerchantNavItems, type MerchantNavSource } from "@/components/ds/use-merchant-nav-items";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/** Grilla del navigation hub mobile: 1 col en muy angosto, 2 cols en el resto. */
export const MERCHANT_MOBILE_NAV_GRID = "grid grid-cols-1 min-[360px]:grid-cols-2 gap-3";

type MerchantMobileNavHubProps = MerchantNavSource & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Offset bajo banners de demo/trial/impersonación. */
  topClassName?: string;
};

function isNavItemActive(pathname: string, to: string) {
  if (to === "/app/dashboard") return pathname === "/app/dashboard" || pathname === "/app";
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Hub de navegación global mobile — cards, no Sidebar / Sheet / Drawer.
 * Fuente de datos: navItemsForRole (mismas rutas, permisos y visibilidad que desktop).
 */
export function MerchantMobileNavHub({
  open,
  onOpenChange,
  role,
  planId,
  businessProfile,
  operationalRoleId,
  topClassName,
}: MerchantMobileNavHubProps) {
  const location = useLocation();
  const navItems = useMerchantNavItems({
    role,
    planId,
    businessProfile,
    operationalRoleId,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="merchant-mobile-nav-title"
      data-testid="merchant-mobile-nav-hub"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[80] flex flex-col bg-background pb-[max(0.75rem,env(safe-area-inset-bottom))]",
        topClassName ?? "top-0",
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Volver
          </button>
          <h2 id="merchant-mobile-nav-title" className="text-sm font-semibold text-foreground">
            Navegación
          </h2>
          <p className="text-xs text-muted-foreground">¿A dónde querés ir?</p>
          <p className="text-xs text-muted-foreground">Accedé a los módulos de Structura.</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Cerrar navegación"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className={MERCHANT_MOBILE_NAV_GRID} data-testid="merchant-mobile-nav-grid">
          {navItems.map((item) => {
            const locked = item.gateState === "locked_feature";
            return (
              <DashboardCard
                key={item.to}
                to={item.to}
                compact
                icon={item.icon}
                title={item.label}
                subtitle={
                  locked && item.minPlanLabel
                    ? `Disponible en ${item.minPlanLabel}`
                    : (item.description ?? "")
                }
                active={isNavItemActive(location.pathname, item.to)}
                onClick={() => onOpenChange(false)}
                className={locked ? "opacity-80" : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
